/**
 * Shared formatting helpers for the admin console.
 *
 * These replace the per-page copies of `inr()` and ad-hoc date formatters that
 * had drifted across Dashboard, PlatformDashboard and RevenueAnalytics.
 */

/**
 * Format a number as compact Indian Rupees.
 * Thresholds: >= 1 Cr, >= 1 L, >= 1 K, else plain.
 *
 * @param {number|string} value
 * @returns {string} e.g. "₹1.50 Cr", "₹2.30 L", "₹4.5K", "₹999"
 */
export const inr = (value) => {
  const num = Number(value) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toLocaleString('en-IN')}`;
};

/**
 * Full-precision rupee amount (no compaction), rounded to whole rupees.
 * @param {number|string} value
 * @returns {string} e.g. "₹1,23,456"
 */
export const inrExact = (value) => {
  const num = Number(value) || 0;
  return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

/**
 * Format an ISO date string as a short, locale-aware date.
 * @param {string|Date|null|undefined} dateStr
 * @returns {string} e.g. "12 Oct 2026" or "—" when empty/invalid
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Format a month key ("2024-01" or "2024-01-01") as "Jan 24".
 * Falls back to the raw input when it cannot be parsed.
 * @param {string} monthStr
 * @returns {string}
 */
export const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  // Only treat strict yyyy-mm or yyyy-mm-dd keys as dates; everything else is
  // returned untouched so stray labels never render as a bogus "Jan 01".
  const isYearMonth = /^\d{4}-\d{2}$/.test(monthStr);
  const isYearMonthDay = /^\d{4}-\d{2}-\d{2}/.test(monthStr);
  if (!isYearMonth && !isYearMonthDay) return monthStr;
  const normalized = isYearMonth ? `${monthStr}-01` : monthStr;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return monthStr;
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

/**
 * Human relative time ("Just now", "5m ago", "3h ago", "2d ago") falling back
 * to a short date beyond a week. Returns "Never" when empty.
 * @param {string|Date|null|undefined} dateStr
 * @returns {string}
 */
export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 'Never';
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
};

/**
 * Readable role names: "org_admin" → "Org admin", "super_admin" → "Super admin".
 * Known roles get a fixed label; anything else is de-snake-cased.
 * @param {string|null|undefined} role
 * @returns {string} '' when empty
 */
const ROLE_LABELS = {
  super_admin: 'Super admin',
  superadmin: 'Super admin',
  org_admin: 'Org admin',
  manager: 'Manager',
  employee: 'Employee',
  agent: 'Agent',
  supervisor: 'Supervisor',
  owner: 'Owner',
  developer: 'Developer',
  support: 'Support',
  sales: 'Sales',
  accounts: 'Accounts',
};

export const roleLabel = (role) => {
  if (!role) return '';
  const key = String(role).toLowerCase();
  if (ROLE_LABELS[key]) return ROLE_LABELS[key];
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * A person's display name: "First Last", else null (so callers can show the
 * email once instead of repeating it).
 * @param {{first_name?: string, last_name?: string, full_name?: string, name?: string}} person
 * @returns {string|null}
 */
export const personName = (person) => {
  if (!person) return null;
  const full = `${person.first_name || ''} ${person.last_name || ''}`.trim();
  return full || person.full_name || person.name || null;
};

/**
 * Plan price label that respects the billing cycle:
 * monthly → "₹999/mo", yearly → "₹9,990/yr".
 * @param {number|string} amount
 * @param {string} cycle  'monthly' | 'yearly' | 'annual' | 'quarterly' …
 */
export const priceWithCycle = (amount, cycle) => {
  const c = String(cycle || 'monthly').toLowerCase();
  const suffix = c.startsWith('year') || c.startsWith('annual') ? '/yr'
    : c.startsWith('quarter') ? '/qtr'
    : c.startsWith('half') ? '/6 mo'
    : '/mo';
  return `${inrExact(amount)}${suffix}`;
};

/**
 * "Chrome on Windows" from a raw user-agent string.
 * @param {string} ua
 * @returns {string}
 */
export const describeUserAgent = (ua) => {
  if (!ua) return '—';
  const s = String(ua);
  let browser = 'Unknown browser';
  if (/Edg\//.test(s)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(s)) browser = 'Opera';
  else if (/SamsungBrowser/.test(s)) browser = 'Samsung Internet';
  else if (/Firefox\//.test(s)) browser = 'Firefox';
  else if (/Chrome\//.test(s)) browser = 'Chrome';
  else if (/Safari\//.test(s)) browser = 'Safari';
  else if (/python-requests|curl|PostmanRuntime|axios|node-fetch|okhttp/i.test(s)) browser = 'Script/API client';

  let os = '';
  if (/Windows/.test(s)) os = 'Windows';
  else if (/iPhone|iPad|iPod/.test(s)) os = 'iOS';
  else if (/Mac OS X|Macintosh/.test(s)) os = 'macOS';
  else if (/Android/.test(s)) os = 'Android';
  else if (/Linux/.test(s)) os = 'Linux';

  return os ? `${browser} on ${os}` : browser;
};
